import React, {FC, useCallback, useEffect} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelTypesEnum} from '../../../../../../libs/shared/core/src/models/api/channelCommonTypes';
import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {useDataEventTracking} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {ThemedImageNamesEnum} from '../../../../../helpers/imageThemeHelpers';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../../models/url/settingsUrlHelpers';
import {getUserNamespace} from '../../../../../store/data/teammates/teammatesSelectors';
import {IntegrationIconsEnum} from '../../../../common/integrationIcon';
import {connectChannelTask} from '../../../../onboarding/tasks/adminOnboardingTasks';
import {StyledCellsEmptySetupActionsContentDiv} from './cellsEmptyCommon';
import {CellsEmptySetupActionsButton} from './cellsEmptySetupActionsButton';
import {CellsEmptySetupActionsDismissButton} from './cellsEmptySetupActionsDismissButton';
import {CtaEmptyState} from './ctaEmptyState';

/*
 * Style.
 */

const StyledTitleDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 6px;
`;

/*
 * Props.
 */

interface TeammateSetupEmptyStateProps {
  navigate: (destination: string) => () => void;
  onDismissClick: () => void;
}

/*
 * Component.
 */

export const TeammateSetupEmptyState: FC<TeammateSetupEmptyStateProps> = (props) => {
  const {navigate, onDismissClick} = props;
  const {trackDataEvent} = useDataEventTracking();
  const namespace = useAppSelector(getUserNamespace);

  useEffect(() => {
    trackDataEvent(DataEventActionsEnum.VIEW, OnboardingDataEventObject.INBOX_EMPTY_STATE_TEAMMATE_CTA);
  }, [trackDataEvent]);

  const newChannelCreationPath = useCallback(
    (channelType: ChannelTypesEnum) =>
      buildSettingsPath({
        namespace,
        rootPage: SettingsRootPagesEnum.CHANNELS,
        resourcePage: SettingsResourcePagesEnum.NEW,
        channelType,
      }),
    [namespace],
  );

  return (
    <CtaEmptyState
      image={ThemedImageNamesEnum.INBOX_CONNECT_PERSONAL_CHANNEL}
      title={
        <StyledTitleDiv>
          <Trans id="cells-empty-cta-teammate-title">Connect your work email</Trans>
          <TooltipCoordinator render={() => connectChannelTask.details} maxWidth={340} placement="top" />
        </StyledTitleDiv>
      }
      description={
        <Trans id="cells-empty-cta-teammate-description">
          Manage personal and shared inboxes inside of Front. Enjoy smoother collaboration, seamless handoffs,
          and eliminate the need to switch between systems.
        </Trans>
      }
    >
      <StyledCellsEmptySetupActionsContentDiv>
        <CellsEmptySetupActionsButton
          buttonVariant={ButtonVariantsEnum.PRIMARY}
          interactionId="cells-empty-cta-teammate-googlebutton"
          title={<Trans id="cells-empty-cta-teammate-googlebutton">Add with Google</Trans>}
          integrationIcon={IntegrationIconsEnum.GOOGLE}
          onClick={() => {
            trackDataEvent(
              DataEventActionsEnum.CLICK,
              OnboardingDataEventObject.INBOX_EMPTY_STATE_TEAMMATE_CTA_ADD_GOOGLE_CHANNEL,
            );
            navigate(newChannelCreationPath(ChannelTypesEnum.GMAIL))();
          }}
        />
        <CellsEmptySetupActionsButton
          buttonVariant={ButtonVariantsEnum.PRIMARY}
          interactionId="cells-empty-cta-teammate-microsoftbutton"
          title={<Trans id="cells-empty-cta-teammate-microsoftbutton">Add with Microsoft</Trans>}
          integrationIcon={IntegrationIconsEnum.MICROSOFT}
          onClick={() => {
            trackDataEvent(
              DataEventActionsEnum.CLICK,
              OnboardingDataEventObject.INBOX_EMPTY_STATE_TEAMMATE_CTA_ADD_MICROSOFT_CHANNEL,
            );
            navigate(newChannelCreationPath(ChannelTypesEnum.OFFICE365))();
          }}
        />
        <CellsEmptySetupActionsButton
          buttonVariant={ButtonVariantsEnum.SECONDARY}
          interactionId="cells-empty-cta-teammate-otherbutton"
          title={<Trans id="cells-empty-cta-teammate-otherbutton">Add other email account</Trans>}
          onClick={() => {
            trackDataEvent(
              DataEventActionsEnum.CLICK,
              OnboardingDataEventObject.INBOX_EMPTY_STATE_TEAMMATE_CTA_ADD_OTHER_CHANNEL,
            );
            navigate(newChannelCreationPath(ChannelTypesEnum.SMTP))();
          }}
        />
        <CellsEmptySetupActionsDismissButton
          interactionId="cells-empty-cta-teammate-dismissbutton"
          onClick={() => {
            trackDataEvent(
              DataEventActionsEnum.CLICK,
              OnboardingDataEventObject.INBOX_EMPTY_STATE_TEAMMATE_CTA_DISMISS,
            );
            onDismissClick();
          }}
        />
      </StyledCellsEmptySetupActionsContentDiv>
    </CtaEmptyState>
  );
};
